import React, { useState, useEffect } from 'react';
import { db, auth } from '../components/Firebase_Config/firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { Dropdown } from './dropdown';
import { riskMapping } from './riskmapping';

const getUserId = () => {
  const user = auth.currentUser;
  return user ? user.uid : null;
};

export const AveragesTable = ({ setOverallAverage, overallAverage }) => {
  const [groupAverages, setGroupAverages] = useState([]);
  const [lowScoreGoals, setLowScoreGoals] = useState({});
  const [activeGroup, setActiveGroup] = useState(null); // for popup modal

  const groupOrder = [
    "Accountability",
    "Transparency",
    "Fairness ",
    "Reliability & Safety",
    "Privacy & Security",
    "Inclusiveness",
    "Risk Management",
    "Impact Assessment",
    "Data Quality Processing Framework",
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const userId = getUserId();
      if (!userId) {
        console.log("User not authenticated");
        return;
      }

      const answersDocRef = doc(db, "Answers", userId);
      const answersSnapshot = await getDoc(answersDocRef);

      if (!answersSnapshot.exists()) {
        console.log("No document found!");
        return;
      }

      const data = answersSnapshot.data()?.ConformityAnalysis;
      if (!data) return;

      const averages = [];
      let totalSum = 0;
      let groupCount = 0;
      const lowGoalsMap = {};

      // Loop through each group
      for (const group in data) {
        const groupData = data[group];
        if (groupData?.GroupAverage !== undefined) {
          const groupAverage = groupData.GroupAverage;
          averages.push({ groupName: group, average: groupAverage });
          totalSum += groupAverage;
          groupCount += 1;
        }

        // Now detect goals under 70%
        const goalsBelowThreshold = [];
        for (const goalKey in groupData) {
          if (goalKey.startsWith("Goal")) {
            const goal = groupData[goalKey];
            if (goal.GoalAverage < 70) {
              // You can attach risks here if you have them
              const risks = goal.Risks || ["No risk data provided"];
              goalsBelowThreshold.push({
                goalName: goalKey,
                goalAverage: goal.GoalAverage,
                risks,
              });
            }
          }
        }
        if (goalsBelowThreshold.length > 0) {
          lowGoalsMap[group] = goalsBelowThreshold;
        }
      }

      const sortedAverages = groupOrder.map((groupName) => {
        const group = averages.find((g) => g.groupName === groupName);
        return group || { groupName, average: 0 };
      });

      const overall = groupCount > 0 ? totalSum / groupCount : 0;

      setGroupAverages(sortedAverages);
      setLowScoreGoals(lowGoalsMap);
      setOverallAverage(overall);

      const userDocQuery = query(collection(db, 'users'), where('userId', '==', userId));
      const userDocSnapshot = await getDocs(userDocQuery);

      if (!userDocSnapshot.empty) {
        const userDocRef = userDocSnapshot.docs[0].ref;
        await updateDoc(userDocRef, { OverallConformityScore: overall });
      }

    } catch (error) {
      console.error("Error fetching data from Firebase:", error);
    }
  };

  return (
    <div className="dashboardCon">
      <table className="Risk-Table">
        <thead>
          <tr>
            <th>Group Name</th>
            <th>Percentage (%)</th>
          </tr>
        </thead>
        <tbody>
          {groupAverages.map((group, index) => {
            const isLow = lowScoreGoals[group.groupName];
            return (
              <tr key={index}>
                <td>
                  {isLow ? (
                    <button
                      className="risk-button attention"
                      onClick={() => setActiveGroup(group.groupName)}
                    >
                      {group.groupName}
                    </button>
                  ) : (
                    group.groupName
                  )}
                </td>
                <td>{group.average.toFixed(1)}%</td>
              </tr>
            );
          })}
          <tr className="totals-row">
            <td>Overall Percentage</td>
            <td>{overallAverage.toFixed(1)}%</td>
          </tr>
        </tbody>
      </table>

      {/* Popup Modal */}
      {activeGroup && (
        <div className="modal-overlay" onClick={() => setActiveGroup(null)}>
          <div className='dashModalContent'>
            <div className="risk-modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{activeGroup} – Goals Below 70%</h2>
              {lowScoreGoals[activeGroup]
                ?.sort((a, b) => a.goalName.localeCompare(b.goalName, undefined, { numeric: true }))
                .map((goal, idx) => {
                  const risks = riskMapping[goal.goalName] || [];
                  return (
                    <Dropdown key={idx} title={`${goal.goalName} (${goal.goalAverage.toFixed(1)}%)`}>
                      {risks.map((risk, rIdx) => (
                        <table key={rIdx} className="risk-table">
                          <tbody>
                            <tr><th>Risk</th><td>{risk.risk}</td></tr>
                            <tr><th>Detail</th><td>{risk.detail}</td></tr>
                            <tr><th>Reference</th><td>{risk.reference}</td></tr>
                          </tbody>
                        </table>
                      ))}
                    </Dropdown>
                  );
                })}
              <button className="close-btn" onClick={() => setActiveGroup(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
